import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Plus, Trash2, GripVertical, Printer, Wand2, ArrowLeft, Search, ArrowUpDown } from 'lucide-react'
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '@/lib/supabase'
import { useSetlistDetail } from '@/hooks/useSetlists'
import { useSongs } from '@/hooks/useSongs'
import { generateSetlist, formatDuration, totalDurationSeconds } from '@/lib/setlistGenerator'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StarRating } from '@/components/StarRating'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from '@/components/ui/select'
import type { Setlist, SetlistItem, Song, SectionSortMode } from '@/types'

type SongSort = SectionSortMode

const SortableItem = ({ item, idx, onRemove, getSongDuration }: {
  item: SetlistItem
  idx: number
  onRemove: (id: string) => void
  getSongDuration: (song?: Song) => number
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className='flex items-center gap-2 rounded border px-3 py-2 text-sm bg-background'>
      <button type='button' className='cursor-grab touch-none text-muted-foreground hover:text-foreground shrink-0' {...attributes} {...listeners}>
        <GripVertical className='h-4 w-4' />
      </button>
      <span className='w-6 text-center text-muted-foreground shrink-0'>{idx + 1}</span>
      <div className='min-w-0 flex-1 truncate'>
        <span className='font-medium'>{item.song?.title ?? 'Unknown'}</span>
        <span className='ml-2 text-muted-foreground'>{item.song?.artist}{item.song?.year ? ` (${item.song.year})` : ''}</span>
      </div>
      <span className='ml-auto shrink-0 text-xs text-muted-foreground tabular-nums font-mono'>
        {formatDuration(getSongDuration(item.song))}
      </span>
      <Button variant='ghost' size='icon' className='h-7 w-7 shrink-0' onClick={() => onRemove(item.id)}>
        <Trash2 className='h-3 w-3' />
      </Button>
    </div>
  )
}

const SongAutocomplete = ({ songs, onSelect, getSongDuration }: {
  songs: Song[]
  onSelect: (songId: string) => void
  getSongDuration: (song?: Song) => number
}) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const filtered = songs.filter((s) => {
    if (!query.trim()) return true
    const q = query.toLowerCase()
    return s.title.toLowerCase().includes(q) || s.artist.toLowerCase().includes(q)
  }).slice(0, 30)

  const handleSelect = (songId: string) => {
    onSelect(songId)
    setQuery('')
    setOpen(false)
    setHighlightIndex(0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || filtered.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleSelect(filtered[highlightIndex].id)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.children[highlightIndex] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex])

  return (
    <div className='relative mt-2'>
      <div className='relative'>
        <Search className='absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground' />
        <Input
          ref={inputRef}
          placeholder='Search repertoire to add...'
          className='pl-8 h-9 text-sm'
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlightIndex(0) }}
          onFocus={() => setOpen(true)}
          onBlur={() => { setTimeout(() => setOpen(false), 150) }}
          onKeyDown={handleKeyDown}
        />
      </div>
      {open && filtered.length > 0 && (
        <div ref={listRef} className='absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border bg-popover shadow-md'>
          {filtered.map((s, i) => (
            <button
              key={s.id}
              type='button'
              className={`flex w-full items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-accent ${i === highlightIndex ? 'bg-accent' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(s.id)}
              onMouseEnter={() => setHighlightIndex(i)}
            >
              <span className='truncate'>
                <span className='font-medium'>{s.title}</span>
                <span className='ml-2 text-muted-foreground'>{s.artist}</span>
              </span>
              <span className='ml-2 shrink-0 text-xs text-muted-foreground'>{formatDuration(getSongDuration(s))}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export const SetlistEditorPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    sections,
    loading,
    addSection,
    removeSection,
    addItem,
    removeItem,
    reorderItems,
    updateSectionSortMode,
    sortSectionItems,
  } = useSetlistDetail(id)

  const [setlist, setSetlist] = useState<Setlist | null>(null)
  const [bandId, setBandId] = useState<string | undefined>()
  const [nameDraft, setNameDraft] = useState('')
  const [venueDraft, setVenueDraft] = useState('')
  const [dateDraft, setDateDraft] = useState('')

  // Load setlist metadata
  useEffect(() => {
    if (!id) return
    supabase.from('setlists').select('*').eq('id', id).single().then(({ data }) => {
      if (data) {
        setSetlist(data as Setlist)
        setBandId(data.band_id)
        setNameDraft(data.name)
        setVenueDraft(data.venue ?? '')
        setDateDraft(data.date)
      }
    })
  }, [id])

  const commitField = async (
    field: 'name' | 'venue' | 'date',
    value: string,
    draftSetter: (v: string) => void,
    requireNonEmpty = false,
  ) => {
    if (!setlist) return
    const next = field === 'date' ? value : value.trim()
    if ((requireNonEmpty && !next) || next === setlist[field]) {
      draftSetter(setlist[field])
      return
    }
    const previous = setlist
    setSetlist({ ...setlist, [field]: next })
    draftSetter(next)
    const { error } = await supabase.from('setlists').update({ [field]: next }).eq('id', setlist.id)
    if (error) {
      setSetlist(previous)
      draftSetter(previous[field])
      toast.error(`Failed to update ${field}`)
    }
  }

  const { songs } = useSongs(bandId)

  const selectedSongIds = useMemo(() => {
    return new Set(sections.flatMap((section) => section.items.map((item) => item.song_id)))
  }, [sections])

  const availableSongs = useMemo(() => {
    return songs.filter((song) => !selectedSongIds.has(song.id))
  }, [songs, selectedSongIds])

  // Auto-generate dialog
  const [showGenerate, setShowGenerate] = useState(false)
  const [genDuration, setGenDuration] = useState('45')
  const [genMinRating, setGenMinRating] = useState(1)
  const [genPreferLess, setGenPreferLess] = useState(true)
  const [genAllowDupes, setGenAllowDupes] = useState(false)
  const [genExcludeOtherSets, setGenExcludeOtherSets] = useState(true)
  const [genTargetSectionId, setGenTargetSectionId] = useState<string | null>(null)

  // Section name
  const [newSectionName, setNewSectionName] = useState('')

  const handleSectionSort = async (sectionId: string, sort: SongSort) => {
    await updateSectionSortMode(sectionId, sort)
    if (sort === 'manual') return
    await sortSectionItems(sectionId, sort)
  }

  const sortLabel = (sort: SongSort) => {
    const labels: Record<SongSort, string> = {
      manual: 'Manual',
      year_desc: 'Year (desc)',
      year_asc: 'Year (asc)',
    }
    return labels[sort]
  }

  const handleAddSection = async () => {
    const nextNum = sections.length + 1
    const name = newSectionName.trim() || `Set ${nextNum}`
    // If there was only one section with no custom name, rename it to 'Set 1'
    if (sections.length === 1 && sections[0].name === 'Set 1') {
      // Already named correctly
    } else if (sections.length === 1) {
      try {
        await supabase.from('setlist_sections').update({ name: 'Set 1' }).eq('id', sections[0].id)
      } catch { /* best effort */ }
    }
    try {
      await addSection(name)
      setNewSectionName('')
      toast.success('Section added')
    } catch {
      toast.error('Failed to add section')
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  )

  const handleDragEnd = async (sectionId: string, event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const section = sections.find((s) => s.id === sectionId)
    if (!section) return
    const oldIndex = section.items.findIndex((i) => i.id === active.id)
    const newIndex = section.items.findIndex((i) => i.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const newOrder = [...section.items.map((i) => i.id)]
    newOrder.splice(oldIndex, 1)
    newOrder.splice(newIndex, 0, active.id as string)
    await reorderItems(sectionId, newOrder)

    const currentSort = section.sort_mode ?? 'manual'
    if (currentSort !== 'manual') {
      await updateSectionSortMode(sectionId, 'manual')
    }
  }

  const handleGenerate = async () => {
    if (!bandId || sections.length === 0) {
      toast.error('Add at least one section first')
      return
    }
    const targetSection = sections.find((s) => s.id === genTargetSectionId) ?? sections[0]

    // Collect song IDs from other sections to exclude
    const excludeSongIds = genExcludeOtherSets
      ? sections
          .filter((s) => s.id !== targetSection.id)
          .flatMap((s) => s.items.map((i) => i.song_id))
      : undefined

    const generated = generateSetlist(songs, {
      bandId,
      targetDurationMinutes: parseInt(genDuration) || 45,
      minimumRating: genMinRating,
      preferLessPlayed: genPreferLess,
      allowDuplicates: genAllowDupes,
      excludeSongIds,
    })
    if (generated.length === 0) {
      toast.error('No songs match the criteria')
      return
    }

    // Batch insert all generated items with correct positions
    const startPos = targetSection.items.length
    const rows = generated.map((item, idx) => ({
      section_id: targetSection.id,
      song_id: item.song.id,
      position: startPos + idx,
    }))
    const { error } = await supabase.from('setlist_items').insert(rows)
    if (error) {
      toast.error('Failed to generate songs')
      return
    }
    toast.success(`Generated ${generated.length} songs`)
    setShowGenerate(false)
  }

  const getSongDuration = (song?: Song) => {
    if (!song) return 0
    return song.performance_duration_seconds ?? song.duration_seconds ?? 180
  }

  if (loading || !setlist) return <div className='p-6'>Loading...</div>

  return (
    <div className='mx-auto max-w-4xl p-6 space-y-6'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <Button variant='ghost' size='icon' onClick={() => navigate('/setlists')}>
            <ArrowLeft className='h-4 w-4' />
          </Button>
          <div className='space-y-0.5'>
            <Input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => commitField('name', nameDraft, setNameDraft, true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
                if (e.key === 'Escape') { setNameDraft(setlist.name); e.currentTarget.blur() }
              }}
              className='h-auto border border-transparent px-2 -mx-2 text-2xl font-bold shadow-none focus-visible:ring-0 focus-visible:border-input'
            />
            <Input
              value={venueDraft}
              placeholder='Venue'
              onChange={(e) => setVenueDraft(e.target.value)}
              onBlur={() => commitField('venue', venueDraft, setVenueDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
                if (e.key === 'Escape') { setVenueDraft(setlist.venue ?? ''); e.currentTarget.blur() }
              }}
              className='h-auto border border-transparent px-2 -mx-2 text-sm text-muted-foreground shadow-none focus-visible:ring-0 focus-visible:border-input'
            />
            <Input
              type='date'
              value={dateDraft}
              onChange={(e) => { setDateDraft(e.target.value); void commitField('date', e.target.value, setDateDraft) }}
              className='h-auto w-auto border border-transparent px-2 -mx-2 text-sm text-muted-foreground shadow-none focus-visible:ring-0 focus-visible:border-input'
            />
          </div>
        </div>
        <Link to={`/setlists/${id}/print`}>
          <Button variant='outline'>
            <Printer className='mr-1 h-4 w-4' />
          </Button>
        </Link>
      </div>

      {/* Sections */}
      {sections.map((section) => {
        const sectionDuration = section.items.reduce((sum, item) => sum + getSongDuration(item.song), 0)
        return (
          <Card key={section.id} className={sections.length === 1 ? 'border-0 shadow-none p-0' : ''}>
            {sections.length > 1 && (
              <CardHeader className='flex flex-row items-center justify-between'>
                <div>
                  <CardTitle className='text-base'>{section.name}</CardTitle>
                  <p className='text-xs text-muted-foreground'>
                    {section.items.length} songs &middot; {formatDuration(sectionDuration)}
                  </p>
                </div>
                <div className='flex gap-1'>
                  <Button size='sm' variant='ghost' onClick={() => { setGenTargetSectionId(section.id); setShowGenerate(true) }}>
                    <Wand2 className='h-3.5 w-3.5' />
                  </Button>
                  <Select value={section.sort_mode ?? 'manual'} onValueChange={(v) => handleSectionSort(section.id, v as SongSort)}>
                    <SelectTrigger className='h-8 w-auto min-w-0 gap-1 px-2 text-xs'>
                      <ArrowUpDown className='h-3.5 w-3.5' />
                      <span>Sort: {sortLabel(section.sort_mode ?? 'manual')}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='manual'>Manual</SelectItem>
                      <SelectItem value='year_desc'>Year (desc)</SelectItem>
                      <SelectItem value='year_asc'>Year (asc)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size='sm' variant='ghost' onClick={() => removeSection(section.id)}>
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              </CardHeader>
            )}
            <CardContent className={`space-y-1 ${sections.length === 1 ? 'px-0' : ''}`}>
              {sections.length === 1 && (
                <div className='flex justify-end gap-1 mb-1'>
                  <Button size='sm' variant='ghost' onClick={() => { setGenTargetSectionId(section.id); setShowGenerate(true) }}>
                    <Wand2 className='h-3.5 w-3.5 mr-1' /> Auto-Populate
                  </Button>
                  <Select value={section.sort_mode ?? 'manual'} onValueChange={(v) => handleSectionSort(section.id, v as SongSort)}>
                    <SelectTrigger className='h-8 w-auto min-w-0 gap-1 px-2 text-xs'>
                      <ArrowUpDown className='h-3.5 w-3.5' />
                      <span className='hidden sm:inline'>Sort: {sortLabel(section.sort_mode ?? 'manual')}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='manual'>Manual</SelectItem>
                      <SelectItem value='year_desc'>Year (desc)</SelectItem>
                      <SelectItem value='year_asc'>Year (asc)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(section.id, e)}>
                <SortableContext items={section.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                  {section.items.map((item, idx) => (
                    <SortableItem
                      key={item.id}
                      item={item}
                      idx={idx}
                      onRemove={removeItem}
                      getSongDuration={getSongDuration}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {section.items.length === 0 && (
                <p className='text-sm text-muted-foreground py-2 text-center'>No songs yet. Search below or use auto-generate.</p>
              )}
              <SongAutocomplete
                songs={availableSongs}
                getSongDuration={getSongDuration}
                onSelect={async (songId) => {
                  try {
                    await addItem(section.id, songId)
                    const currentSort = section.sort_mode ?? 'manual'
                    if (currentSort !== 'manual') {
                      await sortSectionItems(section.id, currentSort)
                    }
                    toast.success('Song added')
                  } catch {
                    toast.error('Failed to add song')
                  }
                }}
              />
            </CardContent>
          </Card>
        )
      })}

      {/* Total */}
      {sections.length > 0 && (
        <div className='text-sm text-muted-foreground text-right'>
          Total: {sections.reduce((sum, s) => sum + s.items.length, 0)} songs &middot;{' '}
          {formatDuration(totalDurationSeconds(sections.flatMap((s) => s.items.map((i) => i.song!)).filter(Boolean)))}
        </div>
      )}

      {/* Add section */}
      <div className='flex gap-2'>
        <Input placeholder='Section name (e.g. Set 2)' value={newSectionName} onChange={(e) => setNewSectionName(e.target.value)} />
        <Button size='sm' variant='outline' onClick={handleAddSection}>
          <Plus className='mr-1 h-3 w-3' /> Add Section
        </Button>
      </div>

      {/* Auto-generate dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Auto-Populate Set</DialogTitle>
            <DialogDescription>Configure generation rules for this set.</DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='space-y-1'>
              <Label>Target duration (minutes)</Label>
              <Input type='number' value={genDuration} onChange={(e) => setGenDuration(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <Label>Minimum rating</Label>
              <StarRating value={genMinRating} onChange={setGenMinRating} />
            </div>
            <div className='flex items-center gap-2'>
              <input type='checkbox' id='preferLess' checked={genPreferLess} onChange={(e) => setGenPreferLess(e.target.checked)} />
              <Label htmlFor='preferLess'>Prefer songs that haven't been played much</Label>
            </div>
            <div className='flex items-center gap-2'>
              <input type='checkbox' id='allowDupes' checked={genAllowDupes} onChange={(e) => setGenAllowDupes(e.target.checked)} />
              <Label htmlFor='allowDupes'>Allow duplicate songs</Label>
            </div>
            {sections.length > 1 && (
              <div className='flex items-center gap-2'>
                <input type='checkbox' id='excludeOtherSets' checked={genExcludeOtherSets} onChange={(e) => setGenExcludeOtherSets(e.target.checked)} />
                <Label htmlFor='excludeOtherSets'>Exclude songs already in other sets</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowGenerate(false)}>Cancel</Button>
            <Button onClick={handleGenerate}>
              <Wand2 className='mr-1 h-4 w-4' /> Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
